import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, X, ExternalLink, Loader2 } from 'lucide-react';

import { docsApi } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { useOpenGuide } from '@/lib/useOpenGuide';
import { RELEASE_NOTES_SIGNATURE, notesForRole } from '@/content/releaseNotes';

/**
 * "Latest Features" — an in-app announcement, in place of a release email.
 *
 * Opens by itself once per person per update: we record the id of the newest
 * entry they have seen, so publishing a new one shows the dialog at their next
 * login and nothing shows again until there is something new. The sidebar link
 * re-opens it on demand, which is also the only way to see it again after
 * dismissing.
 *
 * Seen-state is per browser (localStorage), not per account. Someone on a new
 * machine sees the current entry once more. That is the right trade for an
 * announcement — the cost of an extra read is nil, and the alternative is a
 * schema change to store something this disposable.
 *
 * Content lives in content/releaseNotes.ts; see the header there for how to
 * post an update.
 *
 * The dialog is PORTALED to document.body. The trigger lives in the sidebar,
 * and the sidebar <aside> is transformed (it slides in on mobile via
 * translate-x). A transformed ancestor becomes the containing block for its
 * position:fixed descendants, so rendering the dialog in place pinned it
 * inside a 224px-wide column instead of the viewport — centred, but centred
 * on the sidebar. Portaling moves it out from under that transform so
 * inset-0 means the screen again.
 */

const SEEN_KEY = 'csam-latest-features-seen';

function readSeen(): string | null {
  try { return localStorage.getItem(SEEN_KEY); } catch { return null; }
}
function markSeen(id: string): void {
  try { localStorage.setItem(SEEN_KEY, id); } catch { /* private mode — just show it again */ }
}

export default function LatestFeatures() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { openGuide, busy } = useOpenGuide();

  const notes = notesForRole(user?.role);
  const hasNotes = notes.length > 0;

  // Guides are listed in the dialog so "there's more detail in the guide" is a
  // button rather than an instruction.
  const { data: guideData } = useQuery({
    queryKey: ['docs-guides'],
    queryFn: () => docsApi.list(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });
  const guides = guideData?.guides ?? [];

  // Auto-open once when this browser has not seen the newest entry.
  useEffect(() => {
    if (!user || !hasNotes) return;
    if (readSeen() !== RELEASE_NOTES_SIGNATURE) setOpen(true);
  }, [user, hasNotes]);

  const close = () => {
    markSeen(RELEASE_NOTES_SIGNATURE);
    setOpen(false);
  };

  if (!hasNotes) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100"
      >
        <Sparkles className="h-4 w-4 shrink-0" />
        Latest Features
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={e => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
            <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <div className="flex items-center gap-2.5">
                <span className="rounded-lg bg-blue-50 p-2 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Latest Features</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">What&apos;s new in Curling Stadium Manager</p>
                </div>
              </div>
              <button
                onClick={close}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
              {notes.map(note => (
                <div key={note.id}>
                  <div className="mb-3 flex items-baseline gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{note.title}</h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{note.date}</span>
                  </div>
                  <ul className="space-y-3">
                    {note.items.map((item, i) => (
                      <li key={i} className="border-l-2 border-blue-200 pl-3 dark:border-blue-900">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{item.heading}</p>
                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{item.body}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Standing footer: every announcement points at the guides, so no
                entry has to remember to say it. */}
            <div className="shrink-0 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This is a summary. Step-by-step detail for everything here — and for the rest of the
                system — is in your guide.
              </p>
              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {guides.map(g => (
                    <button
                      key={g.key}
                      onClick={() => openGuide(g.key)}
                      disabled={busy === g.key}
                      title="Opens in a new tab"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {busy === g.key
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <ExternalLink className="h-3.5 w-3.5" />}
                      {g.title}
                    </button>
                  ))}
                </div>
                <button
                  onClick={close}
                  className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
